import * as suryaDesign from "@tSurya/theme/design.surya";

/**
 * Builds a partial styled-components theme fragment populated with Surya brand
 * values (colors + typography). It is merged on top of the active brand theme
 * (MGBW / Global Views) for the Project Details (MyListsDetails) page so that
 * page renders with Surya styling regardless of the current brand domain.
 *
 * Surya itself never uses this fragment - the wrapper short-circuits for the
 * Surya brand - so Surya styling is left completely untouched.
 *
 * The shapes below mirror:
 *  - theme/styleguide/config/colors.ts (buildColors)
 *  - theme/styleguide/components/typography.ts
 * but sourced statically from design.surya instead of the brand-resolved proxy.
 */
export function buildSuryaThemeFragment() {
    const C = suryaDesign.COLORS;
    const T = suryaDesign.TYPOGRAPHY;
    // Surya's true dark text color. The design-file text.main is #333333, but the
    // live Surya page renders dark text as #323132 (its primary/accent dark). We pin
    // every dark-text slot to this so MGBW / Global Views match Surya. Only ever
    // merged onto MGBW / Global Views via the wrapper, so Surya is never affected.
    const darkText = "#323132";

    const colors = {
        primary: {
            main: C.primary.base,
            contrast: C.primary.contrast,
        },
        secondary: {
            main: C.secondary.base,
            contrast: C.white,
        },
        accent: {
            main: C.accent.main,
            contrast: C.accent.contrast,
        },
        background: {
            main: C.background.main,
            contrast: C.background.contrast,
        },
        border: C.border.main,
        disabled: C.disabled.main,
        info: {
            main: C.info.main,
            contrast: C.info.contrast,
        },
        success: {
            main: C.success,
            contrast: C.white,
        },
        danger: {
            main: C.error,
            contrast: C.white,
        },
        warning: {
            main: C.warning,
            contrast: C.white,
        },
        text: {
            main: darkText,
            accent: C.text.accent,
            disabled: C.text.disabled,
            link: C.text.link,
        },
        common: {
            background: C.background.main,
            backgroundContrast: C.background.light,
            accent: C.background.lighter || C.background.light,
            accentContrast: C.text.main,
            border: C.border.main,
            disabled: C.disabled.main,
        },
    };

    const typography = {
        fontFamilyImportUrl: T.fontFamilyImportUrl,
        body: {
            fontFamily: T.body.fontFamily,
            lineHeight: T.body.lineHeight,
            size: T.body.size,
            weight: T.body.weight,
            color: darkText,
            letterSpacingBody: T.body.letterSpacing,
        },
        p: {
            fontFamily: T.p.fontFamily,
            size: T.p.size,
            weight: T.p.weight,
            lineHeight: T.p.lineHeight,
            color: darkText,
            letterSpacing: T.p.letterSpacing,
        },
        h1: {
            size: T.h1.size,
            weight: T.h1.weight,
            lineHeight: T.h1.lineHeight,
            fontFamily: T.h1.fontFamily,
            color: darkText,
        },
        h2: {
            size: T.h2.size,
            weight: T.h2.weight,
            lineHeight: T.h2.lineHeight,
            fontFamily: T.h2.fontFamily,
            color: darkText,
        },
        h3: {
            size: T.h3.size,
            weight: T.h3.weight,
            lineHeight: T.h3.lineHeight,
            fontFamily: T.h3.fontFamily,
            color: darkText,
        },
        h4: {
            size: T.h4.size,
            weight: T.h4.weight,
            lineHeight: T.h4.lineHeight,
            fontFamily: T.h4.fontFamily,
            color: darkText,
        },
        h5: {
            size: T.h5.size,
            weight: T.h5.weight,
            lineHeight: T.h5.lineHeight,
            fontFamily: T.h5.fontFamily,
            color: darkText,
        },
        h6: {
            size: T.h6.size,
            weight: T.h6.weight,
            lineHeight: T.h6.lineHeight,
            fontFamily: T.h6.fontFamily,
            color: darkText,
        },
        headerPrimary: {
            transform: T.headerPrimary.transform,
            size: T.headerPrimary.size,
            weight: T.headerPrimary.weight,
            lineHeight: T.headerPrimary.lineHeight,
            fontFamily: T.headerPrimary.fontFamily,
            color: darkText,
        },
        headerSecondary: {
            size: T.headerSecondary.size,
            transform: T.headerSecondary.transform,
            fontFamily: T.headerSecondary.fontFamily,
            lineHeight: T.headerSecondary.lineHeight,
            weight: T.headerSecondary.weight,
            color: darkText,
        },
        headerTertiary: {
            transform: T.headerTertiary.transform,
            size: T.headerTertiary.size,
            lineHeight: T.headerTertiary.lineHeight,
            weight: T.headerTertiary.weight,
            fontFamily: T.headerTertiary.fontFamily,
            color: darkText,
        },
        legend: {
            size: T.legend.size,
            color: C.text.accent,
            transform: T.legend.transform,
            weight: T.legend.weight,
            fontFamily: T.fontFamily.base,
        },
        link: {
            size: T.link.size,
            weight: T.link.weight,
            lineHeight: T.link.lineHeight,
            color: C.text.link,
        },
    };

    // Mobius Button snapshots a literal color into theme.button[variant].color at
    // app init from the brand-resolved proxy, so it is not covered by the colors
    // override above. We re-pin only the color-bearing fields to Surya values; the
    // contrast/hover/active colors are derived from these automatically by Mobius.
    // The css / typographyProps / mergeCss baked per brand are intentionally left
    // alone so existing button geometry and per-instance css keep working.
    const button = {
        primary: { color: C.darkmagenta },
        secondary: { color: C.darkmagenta },
        link: { background: C.text.link },
    };

    return { colors, typography, button };
}


export default buildSuryaThemeFragment;
